import { useState } from 'react';
import { getApiErrorMessage } from '@/api/errors';
import useProductListMutations from '@/api/mutations/useProductListMutations';
import useGetProductListQuery from '@/api/queries/useGetProductListQuery';
import CustomToast from '@/components/CustomToast';
import PageHeader from '@/components/PageHeader';
import useSearch from '@/components/search/useSearch';
import { StoreAdditiveComboBoxItem } from '@/components/StoreAdditiveComboBox';
import useStagedSelection from '@/hooks/useStagedSelection';
import { Store } from '@/models/Store';
import Breadcrumbs from '@/modules/product/components/Breadcrumbs';
import ProductListDetailHeader from '@/modules/product/components/ProductListDetailHeader';
import ProductListLoadingState from '@/modules/product/components/ProductListLoadingState';
import ProductListStoreAssignmentForm from '@/modules/product/components/ProductListStoreAssignmentForm';
import ProductListStoreTable from '@/modules/product/components/ProductListStoreTable';
import ProductListUnavailableState from '@/modules/product/components/ProductListUnavailableState';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const ProductListStoreList = () => {
  const { productListId } = useParams();
  const { page, handlePageChange } = useSearch();
  const { productList, isLoading, error } = useGetProductListQuery(productListId);
  const { assignStoresToProductList, unassignStoresFromProductList } = useProductListMutations();
  const staged = useStagedSelection<Store>();
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) {
    return <ProductListLoadingState label="Loading Product List" />;
  }

  if (!productListId || !productList || error) {
    return <ProductListUnavailableState />;
  }

  const handleSelect = (item: StoreAdditiveComboBoxItem) => {
    staged.addItem(item.store);
  };

  const handleAssign = async () => {
    if (staged.totalCount === 0) return;

    setSubmitting(true);
    try {
      await assignStoresToProductList({
        productListId,
        ids: staged.stagedIds.map(String),
      });
      staged.clear();
      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message="Selected stores assigned to the Product List."
          visible={t.visible}
        />
      ));
    } catch (caught) {
      const message = await getApiErrorMessage(
        caught,
        'Failed to assign the selected stores. Please try again.',
      );
      toast.custom((t) => (
        <CustomToast id={t.id} message={message} visible={t.visible} variant="error" />
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async (storeId: string) => {
    try {
      await unassignStoresFromProductList({ productListId, ids: [storeId] });
      toast.custom((t) => (
        <CustomToast id={t.id} message="Store removed from the Product List." visible={t.visible} />
      ));
    } catch (caught) {
      const message = await getApiErrorMessage(
        caught,
        'Failed to remove the store. Please try again.',
      );
      toast.custom((t) => (
        <CustomToast id={t.id} message={message} visible={t.visible} variant="error" />
      ));
      throw caught;
    }
  };

  return (
    <div>
      <Breadcrumbs />
      <ProductListDetailHeader productList={productList} />
      <PageHeader title="Store Assignments" />

      <div
        role="note"
        className="mb-4 flex items-start gap-2 rounded-md bg-blue-50 p-4 text-sm text-blue-800"
      >
        <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          Assign an active list here to choose products for a specific store. That store will use
          the lists you assign here instead of the lists assigned to its retailer.
        </p>
      </div>

      <ProductListStoreAssignmentForm
        productListId={productListId}
        supplierId={productList.supplierId}
        selectedIds={staged.stagedIds}
        stagedCount={staged.totalCount}
        onSelect={handleSelect}
        onAssign={handleAssign}
        submitting={submitting}
      />

      <ProductListStoreTable
        productListId={productListId}
        supplierId={productList.supplierId}
        stagedItems={staged.stagedItems}
        onUnstage={staged.removeItem}
        page={page}
        onChangePage={handlePageChange}
        onUnassign={handleUnassign}
      />
    </div>
  );
};

export default ProductListStoreList;
